// gallery.component.ts
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppConfigService } from 'app/services/app-config.service';
import { GalleryService, ImageItem, FolderNode, BrowseResult } from 'app/services/gallery/gallery.service';
import { finalize } from 'rxjs';
import { ImageUrlPipe } from '../../pipe/image-url.pipe';

declare var bootstrap: any;

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule,ImageUrlPipe],
  selector: 'app-gallery',
  templateUrl: './gallery.component.html',
  styleUrls: ['./gallery.component.scss']
})
export class GalleryComponent implements OnInit {

  showLinkModal = false;
  selectedSource: string = '';
  isLinking = false;
  linkResult: string = '';
  linkError: string = '';

  // ── State ──────────────────────────────────────────────────────────────────
  currentPath = '';
  breadcrumbs: { name: string; path: string }[] = [];

  folders: FolderNode[] = [];
  images: ImageItem[] = [];

  searchQuery = '';
  sortMode: 'newest' | 'oldest' | 'name' = 'newest';
  viewMode: 'grid' | 'list' = 'grid';

  // pagination
  pageNumber = 1;
  pageSize = 20;
  totalImages = 0;
  totalPages = 0;
  hasPreviousPage = false;
  hasNextPage = false;

  // upload
  uploading = false;
  uploadPct = 0;
  dragOver = false;

  defaultUrl = '';
  default = '';

  // selection
  get selectedCount() { return this.images.filter(i => i.selected).length; }
  get totalCount() { return this.totalImages; }

  // modals
  active: ImageItem | null = null;
  lightboxOpen = false;
  editOpen = false;
  deleteOpen = false;

  // folder modal
  newFolderOpen = false;
  newFolderName = '';

  // computed
  get filtered(): ImageItem[] {
  let list = [...this.images];

  if (this.sortMode === 'newest')
    list.sort((a, b) => +new Date(b.date) - +new Date(a.date));

  if (this.sortMode === 'oldest')
    list.sort((a, b) => +new Date(a.date) - +new Date(b.date));

  if (this.sortMode === 'name')
    list.sort((a, b) => a.name.localeCompare(b.name));

  return list;
}

  constructor(
    private svc: GalleryService,
    private config: AppConfigService
  ) {
    this.default = this.config.default;
    this.defaultUrl = '';
  }

  ngOnInit() {
    this.load('', 1);
  }

  openLinkModal() {
    this.showLinkModal = true;
  }

  closeLinkModal() {
    if (!this.isLinking) {
      this.showLinkModal = false;
    }
  }

  linkUploads() {
    if (!this.selectedSource || this.isLinking) return;

    this.isLinking = true;
    this.linkResult = '';
    this.linkError = '';

    this.svc.repairUploadsLinks(this.selectedSource)
      .pipe(finalize(() => {
        this.isLinking = false;
      }))
      .subscribe({
        next: (res) => {
          console.log(res);
          this.linkResult = `Done successfully. Processed: ${res?.processed ?? 0}, Updated: ${res?.updated_entities ?? 0}`;

          setTimeout(() => {
            const modalEl = document.getElementById('linkSourceModal');
            if (modalEl) {
              const modalInstance = bootstrap.Modal.getInstance(modalEl) || new bootstrap.Modal(modalEl);
              modalInstance.hide();
            }
          }, 1000);
        },
        error: (err) => {
          this.linkError = err?.error?.message || err?.error || 'Something went wrong while linking uploads.';
        }
      });
  }

  // ── Navigation ─────────────────────────────────────────────────────────────
  load(path: string, page: number = 1) {
  this.currentPath = path;
  this.pageNumber = page;
  this.buildBreadcrumbs(path);

  this.svc.browse(
    path,
    this.pageNumber,
    this.pageSize,
    this.searchQuery
  ).subscribe((res: any) => {
    this.folders = res.folders ?? [];

    this.images = (res.images ?? []).map((img: ImageItem) => ({
      ...img,
      id: img.path,
      size: this.svc.formatSize(img.size as unknown as number),
    }));

    this.totalImages = res.meta?.total_images ?? 0;
    this.totalPages = res.meta?.total_pages ?? 0;
    this.hasPreviousPage = res.meta?.has_previous_page ?? false;
    this.hasNextPage = res.meta?.has_next_page ?? false;
  });
}
onSearchChange() {
  this.load(this.currentPath, 1);
}
  openFolder(folder: FolderNode) {
    this.load(folder.path, 1);
  }

  buildBreadcrumbs(path: string) {
    this.breadcrumbs = [{ name: 'uploads', path: '' }];
    if (!path) return;

    const parts = path.split('/').filter(Boolean);
    let acc = '';

    parts.forEach(p => {
      acc = acc ? `${acc}/${p}` : p;
      this.breadcrumbs.push({ name: p, path: acc });
    });
  }

  navigateTo(crumb: { name: string; path: string }) {
    this.load(crumb.path, 1);
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages || page === this.pageNumber) return;
    this.load(this.currentPath, page);
  }

  nextPage() {
    if (this.hasNextPage) {
      this.load(this.currentPath, this.pageNumber + 1);
    }
  }

  prevPage() {
    if (this.hasPreviousPage) {
      this.load(this.currentPath, this.pageNumber - 1);
    }
  }
// =========================
  // Pagination
  // =========================


  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    const safeTotal = this.totalPages || 1;

    let start = Math.max(1, this.pageNumber - Math.floor(maxVisible / 2));
    let end = Math.min(safeTotal, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  getStartIndex(): number {
    if (this.totalCount === 0) return 0;
    return (this.pageNumber - 1) * this.pageSize + 1;
  }

  getEndIndex(): number {
    const end = this.pageNumber * this.pageSize;
    return Math.min(end, this.totalCount);
  }

  // ── Sort / View ────────────────────────────────────────────────────────────
  setSort(m: 'newest' | 'oldest' | 'name') { this.sortMode = m; }
  setView(m: 'grid' | 'list') { this.viewMode = m; }

  // ── Upload ─────────────────────────────────────────────────────────────────
  onPickFiles(files: FileList | null) {
    if (files?.length) this.doUpload(files);
  }

  onDragOver(e: DragEvent) {
    e.preventDefault();
    this.dragOver = true;
  }

  onDragLeave(e: DragEvent) {
    this.dragOver = false;
  }

  onDrop(e: DragEvent) {
    e.preventDefault();
    this.dragOver = false;
    const files = e.dataTransfer?.files;
    if (files?.length) this.doUpload(files);
  }

  doUpload(files: FileList) {
    this.uploading = true;
    this.uploadPct = 0;

    const tick = setInterval(() => {
      this.uploadPct = Math.min(this.uploadPct + 10, 90);
    }, 150);

    this.svc.upload(files, this.currentPath).subscribe({
      next: () => {
        clearInterval(tick);
        this.uploadPct = 100;
        setTimeout(() => {
          this.uploading = false;
          this.uploadPct = 0;
        }, 600);

        this.load(this.currentPath, 1);
      },
      error: () => {
        clearInterval(tick);
        this.uploading = false;
      },
    });
  }

  // ── Selection ──────────────────────────────────────────────────────────────
  toggleSelect(id: number) {
    const i = this.images.find(x => x.id === id);
    if (i) i.selected = !i.selected;
  }

  toggleSelectAll() {
    const all = this.selectedCount === this.images.length;
    this.images.forEach(i => i.selected = !all);
  }

  clearSelection() {
    this.images.forEach(i => i.selected = false);
  }

  deleteSelected() {
    const toDelete = this.images.filter(i => i.selected);
    toDelete.forEach(img => {
      this.svc.deleteImage(img.path).subscribe(() => {
        this.load(this.currentPath, this.pageNumber);
      });
    });
  }

  // ── Lightbox ───────────────────────────────────────────────────────────────
  openLightbox(img: ImageItem) {
    this.active = img;
    this.lightboxOpen = true;
  }

  closeLightbox() {
    this.lightboxOpen = false;
  }

  // ── Edit ───────────────────────────────────────────────────────────────────
  openEdit(img: ImageItem) {
    this.active = { ...img };
    this.editOpen = true;
  }

  closeEdit() {
    this.editOpen = false;
  }

  saveEdit() {
    if (!this.active) return;

    const nameWithoutExt = this.active.name.replace(/\.[^/.]+$/, '');

    this.svc.renameImage(this.active.path, nameWithoutExt, this.active.desc).subscribe(() => {
      this.load(this.currentPath, this.pageNumber);
      this.closeEdit();
    });
  }

  // ── Delete single ──────────────────────────────────────────────────────────
  openDelete(img: ImageItem) {
    this.active = img;
    this.deleteOpen = true;
  }

  closeDelete() {
    this.deleteOpen = false;
  }

  confirmDelete() {
    if (!this.active) return;

    this.svc.deleteImage(this.active.path).subscribe(() => {
      this.load(this.currentPath, this.pageNumber);
      this.closeDelete();
    });
  }

  // ── Folder actions ─────────────────────────────────────────────────────────
  openNewFolder() {
    this.newFolderName = '';
    this.newFolderOpen = true;
  }

  closeNewFolder() {
    this.newFolderOpen = false;
  }

  createFolder() {
    const name = this.newFolderName.trim();
    if (!name) return;

    this.svc.createFolder(this.currentPath, name).subscribe(() => {
      this.load(this.currentPath, 1);
      this.closeNewFolder();
    });
  }

  deleteFolder(folder: FolderNode) {
    if (!confirm(`حذف الفولدر "${folder.name}" وكل محتوياته؟`)) return;

    this.svc.deleteFolder(folder.path).subscribe(() => {
      this.load(this.currentPath, 1);
    });
  }

  // ── Utility ────────────────────────────────────────────────────────────────
  stop(e: Event) {
    e.stopPropagation();
  }
}